import axios from 'axios';
import { Mutex } from 'async-mutex'; // Import the Mutex class from async-mutex
import store from '../store/store';
import { logout } from '../store/slices/authSlice';
import { REFRESH_TOKEN } from '../configs/routes';

const api = axios.create({
    baseURL: `${process.env.REACT_APP_SCORM_LENS_API_URL}${process.env.REACT_APP_API_VERSION ?? 'v1'}`,
    headers: {
        'Content-Type': 'application/json',
        // "x-api-key": process.env.REACT_APP_BACKENED_API_KEY, // if your API uses API key
    },
    timeout: 600000000000000, // 600 seconds
});

// Create a mutex instance to manage the token refresh process
const mutex = new Mutex();
let refreshTokenPromise = null; // To hold the current refresh token promise
let failedQueue = []; // Queue to store requests waiting for token refresh

// Request Interceptor to attach auth token
api.interceptors.request.use(
    (config) => {
        const authToken = localStorage.getItem('authToken');
        if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response Interceptor to handle token expiration
api.interceptors.response.use(
    (response) => response, // If the response is successful, just return it
    async (error) => {
        const originalRequest = error.config;

        // Check if we encountered a 401 error and the request isn't already retrying
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            if (!refreshTokenPromise) {
                // If no refresh is in progress, acquire the mutex lock
                const release = await mutex.acquire();
                refreshTokenPromise = refreshTokenProcess(originalRequest, release); // Start the token refresh process
                return refreshTokenPromise; // Return the ongoing promise
            } else {
                // If refresh is in progress, queue the request to retry after the refresh
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject, originalRequest });
                });
            }
        }

        return Promise.reject(error); // For other errors, reject the promise
    }
);

// Function to handle the token refresh process
const refreshTokenProcess = async (originalRequest, release) => {
    try {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) throw new Error('No refresh token available');

        // Make an API call to refresh the token
        const response = await axios.post(`${process.env.REACT_APP_SCORM_LENS_API_URL}${REFRESH_TOKEN}`, {
            refresh: refreshToken,
        });

        const { access, refresh } = response?.data;

        // Save the new access & refresh token to localStorage
        if (access) localStorage.setItem('authToken', access);
        if (refresh) localStorage.setItem('refreshToken', refresh);

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        const retryResponse = await axios(originalRequest); // Retry the original request
        processFailedQueue(access); // Process all queued requests
        return retryResponse; // Return the response of the retried request
    } catch (refreshError) {
        handleLogout();
        return Promise.reject(refreshError); // Reject if the refresh fails
    } finally {
        release(); // Release the mutex lock after the process is done
        refreshTokenPromise = null; // Reset the refresh token promise
    }
};

// Function to process the failed requests that were queued
const processFailedQueue = (newAccessToken) => {
    failedQueue.forEach(({ resolve, originalRequest }) => {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        resolve(axios(originalRequest)); // Retry each failed request with the new token
    });
    failedQueue = []; // Clear the queue after processing
};

// Function to handle logout (if refresh fails or no refresh token is available)
const handleLogout = () => {
    try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login'; // Redirect to the login page
        store.dispatch(logout());
    } catch (err) {}
};

export default api;
